import { cn } from "@/shared/utils/core";

interface LoaderProps {
  /**
   * Size variant of the loader
   * - xs: 12px (w-3 h-3)
   * - sm: 16px (w-4 h-4)
   * - md: 24px (w-6 h-6)
   * - lg: 32px (w-8 h-8)
   * - xl: 64px (w-16 h-16)
   */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Optional text label to display below the loader
   */
  label?: string;
  /**
   * Additional CSS classes for the container
   */
  className?: string;
  /**
   * Additional CSS classes for the label text
   */
  labelClassName?: string;
}

const sizeClasses = {
  xs: "h-3 w-3 border-2",
  sm: "h-4 w-4 border-2",
  md: "h-6 w-6 border-[3px]",
  lg: "h-8 w-8 border-[3px]",
  xl: "h-16 w-16 border-4",
};

/**
 * Standardized loading spinner component
 *
 * @example
 * // Simple loader (lg by default)
 * <Loader />
 *
 * @example
 * // With label
 * <Loader label="Loading..." />
 *
 * @example
 * // Small loader inline
 * <Loader size="sm" className="inline-block" />
 */
export default function Loader({ size = "lg", label, className, labelClassName }: LoaderProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2", className)}>
      <div
        className={cn(
          "animate-spin rounded-full border-stone-100 border-t-brand",
          sizeClasses[size],
        )}
        role="status"
        aria-label={label || "Loading"}
      />
      {label && <p className={cn("text-sm text-stone-600", labelClassName)}>{label}</p>}
    </div>
  );
}
